import { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import Ionicons from "@expo/vector-icons/Ionicons";
import { useFocusEffect, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { cuesApi } from "@/api";
import { Logo } from "@/components/brand/logo";
import { isCompactWidth } from "@/constants/layout";
import { Avatar } from "@/components/ui/avatar";
import { Loading } from "@/components/ui/loading";
import { socialStrings as strings } from "@/i18n/features/social.i18n";
import { confirm, notify } from "@/lib/dialog";
import { useSessionStore } from "@/state/session-store";
import { useAppTheme } from "@/theme/app-theme";
// `CueCounts` is the API's own shape. It was declared locally here and drifted
// the moment the server started returning allowances instead of daily totals.
import type { Cue, CueCounts } from "@/types";
import { handleFor } from "@/lib/profile-handle";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

const BRAND_BLUE = "#2468D8";
const BRAND_BLUE_SHADOW = "#205BBC";

type CueStatus = "accepted" | "ignored" | "blocked";

function getInboxColors(isDark: boolean) {
  return {
    screen: isDark ? "#0D1724" : "#FCFDFF",
    topBar: isDark ? "#101C2C" : "#FCFDFF",

    surface: isDark ? "#152235" : "#FFFFFF",
    surfaceMuted: isDark ? "#19283B" : "#FAFBFD",
    surfaceSoft: isDark ? "#1B2C43" : "#F4F8FF",
    surfacePressed: isDark ? "#213650" : "#F4F8FF",

    textPrimary: isDark ? "#EFF5FF" : "#102D51",
    textStrong: isDark ? "#DFEAFA" : "#153252",
    textBody: isDark ? "#C0CDDD" : "#425873",
    textSecondary: isDark ? "#A8B6C9" : "#7B879A",
    textMuted: isDark ? "#8999AD" : "#929DAC",

    border: isDark ? "#2C3C51" : "#E1E8F1",
    borderSoft: isDark ? "#29394D" : "#E4EAF2",
    divider: isDark ? "#2A3A4E" : "#E3E9F1",

    primary: isDark ? "#6EA8FF" : "#2468D8",
    primaryStrong: isDark ? "#91BCFF" : "#2463CB",
    primaryText: isDark ? "#B9D3FF" : "#3568B7",
    primarySoft: isDark ? "#203754" : "#F0F5FD",
    primarySurface: isDark ? "#1D314B" : "#F3F7FF",
    primaryBorder: isDark ? "#34577F" : "#DDE7F7",

    iconNeutral: isDark ? "#A9B9CD" : "#526C8D",

    green: isDark ? "#57C98D" : "#35A86C",

    danger: isDark ? "#FF929B" : "#A64A54",
    dangerStrong: isDark ? "#FF7D88" : "#B4232F",
    dangerSoft: isDark ? "#3A222B" : "#FFF3F4",
    dangerSoftStrong: isDark ? "#42232D" : "#FFF0F1",

    shadow: isDark ? "#000000" : "#7187A6",

    white: "#FFFFFF",
  };
}

type InboxColors = ReturnType<typeof getInboxColors>;

type InboxStyles = ReturnType<typeof createStyles>;

type CueRow = { kind: "received"; cue: Cue } | { kind: "sent"; cue: Cue };

export default function Cues() {
  const router = useRouter();
  const { t } = useTranslation();
  const { isDark } = useAppTheme();

  // Same reason as the Inbox header: the wordmark is absolutely positioned and
  // sits under the centred filter chips, so on a phone-sized viewport they
  // overlap it. `useWindowDimensions` re-renders on resize, which a one-time
  // `Dimensions.get` would not.
  //
  // Only the wordmark is compact-aware here. This screen's filter chips are
  // already identical on web and native — no `flex` branch, unlike Inbox — so
  // there is nothing for narrow web to borrow, and touching them would change
  // something that already works.
  const { width } = useWindowDimensions();
  const showLogo = Platform.OS === "web" && !isCompactWidth(width);

  const colors = useMemo(() => getInboxColors(isDark), [isDark]);

  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);

  const inbox = useSessionStore((state) => state.inbox);

  const sentCues = useSessionStore((state) => state.sentCues);

  const refreshInbox = useSessionStore((state) => state.refreshInbox);

  const refreshSentCues = useSessionStore((state) => state.refreshSentCues);

  const respondToCue = useSessionStore((state) => state.respondToCue);

  const cancelSentCue = useSessionStore((state) => state.cancelSentCue);

  const [tab, setTab] = useState<"received" | "sent">("received");

  const [counts, setCounts] = useState<CueCounts>({
    connectionsUsed: 0,
    connectionsLimit: 0,
    connectionsAvailable: 0,
    recipientsUsed: 0,
    recipientsLimit: 0,
    recipientsAvailable: 0,
    pending: 0,
  });

  // Until the first fetch lands there is nothing to say: an empty list and a
  // list not yet loaded look identical, and "no cues yet" is a claim we cannot
  // make until we have asked. Answered from the store, not a local flag —
  // `refreshAll` runs these same fetches at session load, and a local flag
  // that cannot see them held a first visit on a spinner re-asking a question
  // already answered. The focus fetch below still refreshes in place.
  const loaded = useSessionStore(
    (state) => state.inboxSettled && state.sentCuesSettled,
  );

  // Counts stay screen-local (deliberately NOT prefetched by refreshAll — a
  // request that is pointless for anyone who never opens this tab), so the
  // banner below that fires on `connectionsAvailable === 0` needs its own
  // gate — and it is MEASURED, not merely attempted: the initial zeros are a
  // placeholder, and a failed read leaves them zeros. A settled-style flag
  // flipped in a finally would paint the out-of-slots warning from that
  // placeholder for exactly the visitors whose first read failed. Missing a
  // true warning is recoverable — the focus effect re-asks on every visit —
  // while a false one greets a new user with a limit they never touched.
  const [countsMeasured, setCountsMeasured] = useState(false);

  const refreshCounts = useCallback(async () => {
    try {
      setCounts(await cuesApi.counts());
      setCountsMeasured(true);
    } catch {
      // Non-critical: leave the last known counts in place.
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      // allSettled: one failing call must not hold the screen on a spinner.
      void Promise.allSettled([refreshInbox(), refreshSentCues(), refreshCounts()]);
    }, [refreshInbox, refreshSentCues, refreshCounts]),
  );

  const rows = useMemo<CueRow[]>(
    () =>
      tab === "received"
        ? inbox.map((cue) => ({
            kind: "received" as const,
            cue,
          }))
        : sentCues.map((cue) => ({
            kind: "sent" as const,
            cue,
          })),
    [tab, inbox, sentCues],
  );

  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);

  /**
   * Withdraw, via `lib/dialog` rather than `Alert` directly.
   *
   * `react-native-web`'s Alert is `static alert() {}` — an empty function, not a
   * degraded one. So on web this button did nothing at all: the confirmation
   * never appeared, its `onPress` never ran, and the failure message in the
   * catch was equally silent. The tap looked like a dead control.
   *
   * Confirmed at all, because the cooldown is the part nobody expects: the slot
   * comes back but that person does not, and cannot be cued again for hours.
   */
  async function withdraw(cue: Cue) {
    const confirmed = await confirm({
      title: t(strings.withdrawConfirmTitle),
      message: t(strings.withdrawConfirmBody),
      confirmLabel: t(strings.withdrawConfirm),
      cancelLabel: t(strings.cancel),
      destructive: true,
    });
    if (!confirmed) return;

    setWithdrawingId(cue.id);
    try {
      await cancelSentCue(cue.id);
      await refreshCounts();
    } catch {
      notify(t(strings.withdrawFailed));
    } finally {
      setWithdrawingId(null);
    }
  }

  async function respond(cue: Cue, status: CueStatus) {
    const chat = await respondToCue(cue.id, status);
    void refreshCounts();

    if (chat) {
      router.push(`/chat/${chat.id}`);
    }
  }

  return (
    <SafeAreaView style={styles.safe} edges={["top"]}>
      <StatusBar style={isDark ? "light" : "dark"} />

      <View style={styles.topBar}>
        {showLogo ? (
          <View style={styles.topBarLogo}>
            <Logo size={24} />
          </View>
        ) : null}

        <CueTabs
          tab={tab}
          onChange={setTab}
          // The lists themselves. A number on a tab is read as "how many rows
          // are in there", so it can be a badge or a total but never both —
          // and the earlier version was a badge wearing the label of a total.
          // These are the list lengths, which is the only reading that cannot
          // contradict what you see when you tap.
          //
          // Sent counts more than it used to, because the list does: it is a
          // ledger of pending AND recently accepted Cues, so an accepted Cue no
          // longer disappears the instant it succeeds.
          receivedCount={inbox.length}
          sentCount={sentCues.length}
          pendingCount={counts.pending}
          colors={colors}
          styles={styles}
        />
      </View>

      {/*
        The running totals used to live here: "4 of 5 Cue slots free" and "1 of
        10 people reached today". Two budgets, counted in opposite directions —
        one showed what was LEFT, the other what was SPENT — displayed to
        someone who, nearly always, was nowhere near either. The tab already
        says how many Cues are out.

        What survives is the one case where the rule is the thing standing in
        the way. Out of slots, the list looks the same as a quiet day, and the
        reason a send will be refused is worth saying before it is refused.
      */}
      {tab === "sent" && countsMeasured && counts.connectionsAvailable === 0 ? (
        <View style={styles.allowanceBar}>
          <Text style={styles.allowanceHelp}>{t(strings.slotsHelp)}</Text>
        </View>
      ) : null}

      <FlatList
        data={rows}
        keyExtractor={(item) => `${item.kind}:${item.cue.id}`}
        showsVerticalScrollIndicator={false}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
        contentContainerStyle={[
          styles.list,
          rows.length === 0 && styles.emptyList,
        ]}
        renderItem={({ item }) =>
          item.kind === "received" ? (
            <CueRequest
              cue={item.cue}
              onRespond={respond}
              onOpenProfile={() =>
                router.push(
                  `/guest-profile?v=${handleFor(item.cue.fromUserId)}`,
                )
              }
              colors={colors}
              styles={styles}
            />
          ) : (
            <SentCueRow
              cue={item.cue}
              onOpenProfile={() =>
                router.push(
                  `/guest-profile?v=${handleFor(item.cue.toUserId)}`,
                )
              }
              onWithdraw={() => void withdraw(item.cue)}
              onOpenChat={() => {
                if (item.cue.chatId) router.push(`/chat/${item.cue.chatId}`);
              }}
              busy={withdrawingId === item.cue.id}
              colors={colors}
              styles={styles}
            />
          )
        }
        ListEmptyComponent={
          loaded ? (
            <EmptyInbox colors={colors} styles={styles} />
          ) : (
            <Loading color={colors.primary} />
          )
        }
      />
    </SafeAreaView>
  );
}

/**
 * The chip icons scale with their label, instead of standing still beside it.
 *
 * `@expo/vector-icons` renders a glyph as a `<Text>` and sets
 * `allowFontScaling: false` in its own `defaultProps` (create-icon-set.js:37 —
 * a class component, so React 19 still honours it). Every icon in the app is
 * therefore pinned at 1x while the text beside it follows the OS setting. In a
 * chip that is a visible drift: the word grows, the mail glyph does not, and
 * the pair stops looking like one control.
 *
 * Turning scaling ON is enough to bind them, because the icon is a Text like
 * any other and so takes the same app-wide ceiling from
 * patches/react-native+0.85.3.patch. Icon and label then share a floor of 1x
 * and a cap of 1.2x — they move together or not at all.
 *
 * Deliberately scoped to this strip rather than made the app-wide default. An
 * icon that is a fixed-size shape (a tab bar glyph, a badge, an avatar
 * overlay) is right to stay put; one that reads as part of a word is not.
 */
const TAB_ICON_SCALES = true;

function CueTabs({
  tab,
  onChange,
  receivedCount,
  sentCount,
  pendingCount,
  colors,
  styles,
}: {
  tab: "received" | "sent";
  onChange: (tab: "received" | "sent") => void;
  receivedCount: number;
  sentCount: number;
  pendingCount: number;
  colors: InboxColors;
  styles: InboxStyles;
}) {
  const { t } = useTranslation();
  const hasRequests = pendingCount > 0;
  // A zero next to a label is noise — it states the list is empty to somebody
  // who is one tap from seeing that, and it makes a quiet day look like a
  // reading rather than a rest. Non-zero only, on both tabs.
  const count = (n: number) => (n > 0 ? ` ${n}` : "");
  return (
    <View style={styles.tabs}>
      <Pressable
        accessibilityRole="tab"
        accessibilityState={{ selected: tab === "received" }}
        accessibilityLabel={t(strings.receivedA11y)}
        onPress={() => onChange("received")}
        style={[styles.tabButton, tab === "received" && styles.tabButtonActive]}
      >
        <Ionicons
          name="mail-open-outline"
          size={15}
          allowFontScaling={TAB_ICON_SCALES}
          color={tab === "received" ? colors.white : colors.textSecondary}
        />

        <Text
          style={[
            styles.tabButtonText,
            tab === "received" && styles.tabButtonTextActive,
          ]}
        >
          {t(strings.received)}
          {count(receivedCount)}
        </Text>
      </Pressable>

      <Pressable
        accessibilityRole="tab"
        accessibilityState={{ selected: tab === "sent" }}
        accessibilityLabel={t(strings.sentA11y)}
        onPress={() => onChange("sent")}
        style={[styles.tabButton, tab === "sent" && styles.tabButtonActive]}
      >
        <Ionicons
          name="paper-plane-outline"
          size={15}
          allowFontScaling={TAB_ICON_SCALES}
          color={tab === "sent" ? colors.white : colors.textSecondary}
        />

        <Text
          style={[
            styles.tabButtonText,
            tab === "sent" && styles.tabButtonTextActive,
          ]}
        >
          {t(strings.sent)}
          {count(sentCount)}
        </Text>
      </Pressable>

      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(strings.newRequests, { count: pendingCount })}
        onPress={() => onChange("received")}
        style={[
          styles.tabButton,
          styles.requestButton,
          hasRequests && styles.requestButtonActive,
        ]}
      >
        <Ionicons
          name={hasRequests ? "notifications" : "notifications-outline"}
          size={15}
          allowFontScaling={TAB_ICON_SCALES}
          color={hasRequests ? colors.white : colors.textSecondary}
        />

        <Text
          style={[
            styles.tabButtonText,
            hasRequests && styles.tabButtonTextActive,
          ]}
        >
          {pendingCount}
        </Text>
      </Pressable>
    </View>
  );
}

function CueRequest({
  cue,
  onRespond,
  onOpenProfile,
  colors,
  styles,
}: {
  cue: Cue;
  onRespond: (cue: Cue, status: CueStatus) => Promise<void>;
  onOpenProfile: () => void;
  colors: InboxColors;
  styles: InboxStyles;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const [activeAction, setActiveAction] = useState<CueStatus | null>(null);
  const displayName = cue.fromName ?? t("social.notifications.someoneNearby");
  const title = cue.fromVenueTag
    ? `${displayName} · ${cue.fromVenueTag}`
    : displayName;
  const goals = cue.fromLookingFor?.filter(Boolean).slice(0, 3) ?? [];
  const interests = cue.fromInterests?.filter(Boolean).slice(0, 3) ?? [];
  const details = [
    cue.fromAgeBand,
    goals.length ? ` ${goals.join(" · ")}` : null,
  ].filter(Boolean);

  async function act(status: CueStatus) {
    setBusy(true);
    setActiveAction(status);

    try {
      await onRespond(cue, status);
    } finally {
      setBusy(false);
      setActiveAction(null);
    }
  }

  return (
    <View style={styles.requestCard}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(strings.viewProfile, { name: displayName })}
        onPress={onOpenProfile}
        style={({ hovered, pressed }) => [
          styles.requestTop,
          Platform.OS === "web" && hovered && styles.rowHovered,
          pressed && styles.buttonPressed,
        ]}
      >
        <View style={styles.avatarWrap}>
          <Avatar
            seed={cue.fromAvatarSeed ?? cue.fromUserId}
            avatarConfig={cue.fromAvatarConfig}
            name={displayName}
            size={58}
          />

          <View style={styles.nearbyBadge}>
            <Ionicons name="location" size={10} color={colors.white} />
          </View>
        </View>

        <View style={styles.requestInfo}>
          <View style={styles.requestTitleRow}>
            <Text style={styles.requestTitle} numberOfLines={1}>
              {title}
            </Text>
          </View>

          {details.length ? (
            <Text style={styles.requestDescription} numberOfLines={1}>
              {details.join(" · ")}
            </Text>
          ) : null}
        </View>
      </Pressable>

      {interests.length ? (
        <View style={styles.requestMessage}>
          <View style={styles.quoteIcon}>
            <Ionicons
              name="chatbubble-ellipses-outline"
              size={17}
              color={colors.primary}
            />
          </View>

          <Text style={styles.requestMessageText}>{interests.join(" · ")}</Text>
        </View>
      ) : null}

      <View style={styles.primaryActions}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.acceptA11y)}
          disabled={busy}
          onPress={() => act("accepted")}
          style={({ pressed }) => [
            styles.acceptButton,
            pressed && !busy && styles.buttonPressed,
            busy && styles.buttonDisabled,
          ]}
        >
          {activeAction === "accepted" ? (
            <ActivityIndicator size="small" color={colors.white} />
          ) : (
            <Ionicons name="checkmark" size={17} color={colors.white} />
          )}

          <Text style={styles.acceptButtonText}>{t(strings.accept)}</Text>
        </Pressable>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.ignoreA11y)}
          disabled={busy}
          onPress={() => act("ignored")}
          style={({ pressed }) => [
            styles.ignoreButton,
            pressed && !busy && styles.ignoreButtonPressed,
            busy && styles.buttonDisabled,
          ]}
        >
          {activeAction === "ignored" ? (
            <ActivityIndicator size="small" color={colors.primaryStrong} />
          ) : (
            <Ionicons name="close" size={16} color={colors.primaryStrong} />
          )}

          <Text style={styles.ignoreButtonText}>{t(strings.ignore)}</Text>
        </Pressable>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(strings.blockA11y)}
          disabled={busy}
          onPress={() => act("blocked")}
          style={({ pressed }) => [
            styles.blockButton,
            pressed && !busy && styles.blockButtonPressed,
            busy && styles.buttonDisabled,
          ]}
        >
          {activeAction === "blocked" ? (
            <ActivityIndicator size="small" color={colors.danger} />
          ) : (
            <Ionicons name="ban-outline" size={16} color={colors.danger} />
          )}

          <Text style={styles.blockButtonText}>{t(strings.block)}</Text>
        </Pressable>
      </View>
    </View>
  );
}

function SentCueRow({
  cue,
  onOpenProfile,
  onWithdraw,
  onOpenChat,
  busy,
  colors,
  styles,
}: {
  cue: Cue;
  onOpenProfile: () => void;
  onWithdraw: () => void;
  onOpenChat: () => void;
  busy: boolean;
  colors: InboxColors;
  styles: InboxStyles;
}) {
  const { t } = useTranslation();
  // The Sent list is a ledger now, so a row is one of two things. Read off the
  // status rather than assumed: the old row was pending-only and every label on
  // it said "waiting", which is a false statement on an accepted Cue.
  const accepted = cue.status === "accepted";
  const displayName = cue.toName ?? "Someone";
  const title = cue.toVenueTag
    ? `${displayName} · ${cue.toVenueTag}`
    : displayName;
  const goals = cue.toLookingFor?.filter(Boolean).slice(0, 3) ?? [];
  const details = [
    cue.toAgeBand,
    goals.length ? `${goals.join(" · ")}` : null,
  ].filter(Boolean);

  return (
    <View style={styles.requestCard}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t(strings.viewProfile, { name: displayName })}
        onPress={onOpenProfile}
        style={({ hovered, pressed }) => [
          styles.requestTop,
          Platform.OS === "web" && hovered && styles.rowHovered,
          pressed && styles.buttonPressed,
        ]}
      >
        <View style={styles.avatarWrap}>
          <Avatar
            seed={cue.toAvatarSeed ?? cue.toUserId}
            avatarConfig={cue.toAvatarConfig}
            name={displayName}
            size={58}
          />

          <View style={styles.nearbyBadge}>
            <Ionicons
              name={accepted ? "chatbubble-ellipses" : "paper-plane"}
              size={10}
              color={colors.white}
            />
          </View>
        </View>

        <View style={styles.requestInfo}>
          <View style={styles.requestTitleRow}>
            <Text style={styles.requestTitle} numberOfLines={1}>
              {title}
            </Text>

            <View
              style={[
                styles.anonymousBadge,
                accepted && styles.acceptedBadge,
              ]}
            >
              {/*
                No glyph on the accepted chip. The word "Accepted", in green,
                on a green field already says it three ways over, and a check
                here would be the second check on a row that is about to carry
                the verification badge beside the name. That badge is the one
                check in the product that has to mean something specific, and
                it loses that the moment checks are ordinary decoration.

                The waiting chip keeps its clock: "Waiting" describes a state
                that has not resolved, and the glyph is the only thing marking
                it as ongoing rather than final.
              */}
              {accepted ? null : (
                <Ionicons
                  name="time-outline"
                  size={11}
                  color={colors.iconNeutral}
                />
              )}

              <Text
                style={[
                  styles.anonymousText,
                  accepted && styles.acceptedBadgeText,
                ]}
              >
                {t(accepted ? strings.acceptedBadge : strings.waiting)}
              </Text>
            </View>
          </View>

          {details.length ? (
            <Text style={styles.requestDescription} numberOfLines={1}>
              {details.join(" · ")}
            </Text>
          ) : null}

          {cue.toHeadline ? (
            <Text style={styles.requestDescription} numberOfLines={2}>
              {cue.toHeadline}
            </Text>
          ) : null}

          {/*
            "They accepted your Cue" is cut: the Accepted chip above already
            says it, and the row then said the same thing twice before offering
            the button that acts on it.

            The waiting line stays. "Waiting" on its own is a status, and the
            sentence is where the sender is told what happens next — which is
            not something the chip can carry.
          */}
          {accepted ? null : (
            <Text style={styles.requestDescription}>
              {t(strings.waitingBody)}
            </Text>
          )}
        </View>
      </Pressable>

      <View style={styles.sentActions}>
        {accepted ? (
          /*
            No Withdraw on an accepted Cue — there is nothing left to take
            back, and the conversation is the thing the sender came here to
            find. `chatId` can be null if the chat has since been ended and
            cleaned up, and then this is a plain row with no action rather
            than a button that goes nowhere.
          */
          cue.chatId ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t(strings.openConversationA11y, {
                name: displayName,
              })}
              onPress={onOpenChat}
              style={({ pressed }) => [
                styles.acceptButton,
                pressed && styles.buttonPressed,
              ]}
            >
              <Ionicons
                name="chatbubble-ellipses-outline"
                size={16}
                color={colors.white}
              />

              <Text style={styles.acceptButtonText}>
                {t(strings.openChat)}
              </Text>
            </Pressable>
          ) : null
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t(strings.withdrawA11y)}
            disabled={busy}
            onPress={onWithdraw}
            style={({ pressed }) => [
              styles.ignoreButton,
              pressed && !busy && styles.ignoreButtonPressed,
              busy && styles.buttonDisabled,
            ]}
          >
            {busy ? (
              <ActivityIndicator size="small" color={colors.primaryStrong} />
            ) : (
              <Ionicons
                name="arrow-undo-outline"
                size={16}
                color={colors.primaryStrong}
              />
            )}

            <Text style={styles.ignoreButtonText}>{t(strings.withdraw)}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

function EmptyInbox({
  colors,
  styles,
}: {
  colors: InboxColors;
  styles: InboxStyles;
}) {
  const { t } = useTranslation();
  return (
    <View style={styles.empty}>
      <View style={styles.emptyGraphic}>
        <View style={styles.emptyOuterRing} />

        <View style={styles.emptyMiddleRing} />

        <View style={styles.emptyIcon}>
          <Ionicons name="mail-open-outline" size={31} color={colors.primary} />
        </View>

        <View style={styles.emptyCheckBadge}>
          <Ionicons name="checkmark" size={16} color={colors.white} />
        </View>
      </View>

      <Text style={styles.emptyTitle}>{t(strings.cuesEmptyTitle)}</Text>

      <Text style={styles.emptyDescription}>{t(strings.cuesEmptyBody)}</Text>
    </View>
  );
}

function createStyles(colors: InboxColors, isDark: boolean) {
  return StyleSheet.create({
    safe: {
      flex: 1,
      backgroundColor: colors.screen,
    },

    topBar: {
      minHeight: 62,
      paddingHorizontal: 16,
      paddingVertical: 11,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.divider,
      backgroundColor: colors.topBar,
    },

    topBarLogo: {
      position: "absolute",
      left: 24,
    },

    topBarMessagePill: {
      maxWidth: "68%",
      minHeight: 44,
      paddingHorizontal: 13,
      paddingVertical: 6,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 18,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.primarySurface,
    },

    topBarMessage: {
      color: colors.textBody,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 17,
      fontWeight: "700",
      textAlign: "center",
    },

    inboxBadge: {
      position: "absolute",
      right: 24,
      minHeight: 34,
      paddingHorizontal: 12,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      borderRadius: 17,
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.surfaceSoft,
    },

    inboxBadgeText: {
      color: colors.primaryText,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "700",
    },

    list: {
      width: "100%",
      maxWidth: 680,
      alignSelf: "center",
      paddingHorizontal: 22,
      paddingTop: 24,
      paddingBottom: 55,
      gap: 0,
    },

    emptyList: {
      flexGrow: 1,
    },

    tabs: {
      flexDirection: "row",
      // Wraps rather than overflowing. These chips are content-sized in a row
      // that neither wrapped nor scrolled, so at a large text setting their
      // combined width exceeded the screen and the ends were cut off —
      // "Received" rendered as "Receivec". A second line costs vertical space
      // the screen has; the cut costs the word.
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    },

    tabButton: {
      minHeight: 40,
      paddingHorizontal: 13,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      borderRadius: 13,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surface,
    },

    tabButtonActive: {
      borderColor: BRAND_BLUE,
      backgroundColor: BRAND_BLUE,
      shadowColor: BRAND_BLUE_SHADOW,
      shadowOpacity: 0.2,
      shadowRadius: 9,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 3,
    },

    tabButtonText: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "700",
    },

    tabButtonTextActive: {
      color: colors.white,
    },

    requestButton: {
      paddingHorizontal: 11,
    },

    requestButtonActive: {
      borderColor: colors.dangerStrong,
      backgroundColor: colors.dangerStrong,
      shadowColor: colors.dangerStrong,
      shadowOpacity: 0.22,
      shadowRadius: 9,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 3,
    },

    sectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 14,
      marginTop: 2,
    },

    sectionTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 21,
      lineHeight: 27,
      fontWeight: "700",
      letterSpacing: -0.4,
    },

    sectionDescription: {
      marginTop: 3,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 18,
    },

    newBadge: {
      minHeight: 30,
      paddingHorizontal: 10,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      borderRadius: 15,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.surfaceMuted,
    },

    newDot: {
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: colors.green,
    },

    newText: {
      color: colors.green,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "700",
    },

    requestCard: {
      paddingVertical: 10,
      gap: 12,
      backgroundColor: "transparent",
    },

    /*
      A rule BETWEEN rows, which is what a divider is. It used to be a bottom
      border on the card, so every row drew one including the last — a line
      under a single Cue, separating it from nothing.

      As `ItemSeparatorComponent` the list draws it n-1 times, so the one-row
      case needs no special casing and cannot regress into one.
    */
    separator: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: colors.divider,
    },

    requestTop: {
      minHeight: 74,
      paddingHorizontal: 10,
      paddingVertical: 8,
      flexDirection: "row",
      alignItems: "center",
      gap: 13,
      borderRadius: 12,
    },

    rowHovered: {
      backgroundColor: colors.surfaceMuted,
    },

    avatarWrap: {
      position: "relative",
    },

    nearbyBadge: {
      position: "absolute",
      right: -1,
      bottom: 0,
      width: 20,
      height: 20,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 3,
      borderColor: colors.surface,
      backgroundColor: BRAND_BLUE,
    },

    requestInfo: {
      flex: 1,
      gap: 4,
    },

    requestTitleRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 7,
    },

    requestTitle: {
      flexShrink: 1,
      color: colors.textStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      lineHeight: 22,
      fontWeight: "700",
    },

    anonymousBadge: {
      minHeight: 23,
      paddingHorizontal: 7,
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      borderRadius: 12,
      backgroundColor: colors.surfaceMuted,
    },

    anonymousText: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 9,
      fontWeight: "800",
      textTransform: "uppercase",
      letterSpacing: 0.3,
    },

    // Same badge, the one state on this screen worth colouring. Everything else
    // in the Sent list is a neutral "still waiting"; this is the outcome the
    // list exists to report.
    acceptedBadge: {
      backgroundColor: colors.primarySoft,
    },

    acceptedBadgeText: {
      color: colors.green,
    },

    requestDescription: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      lineHeight: 19,
    },

    requestMessage: {
      minHeight: 50,
      paddingHorizontal: 13,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: colors.borderSoft,
      backgroundColor: colors.surfaceMuted,
    },

    quoteIcon: {
      width: 31,
      height: 31,
      borderRadius: 10,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primarySoft,
    },

    requestMessageText: {
      flex: 1,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      lineHeight: 17,
    },

    primaryActions: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },

    acceptButton: {
      flex: 1,
      minHeight: 38,
      paddingHorizontal: 8,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      borderRadius: 19,
      backgroundColor: BRAND_BLUE,
      shadowColor: BRAND_BLUE_SHADOW,
      shadowOpacity: 0.12,
      shadowRadius: 6,
      shadowOffset: {
        width: 0,
        height: 3,
      },
      elevation: 2,
    },

    acceptButtonText: {
      color: colors.white,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "700",
    },

    allowanceBar: {
      paddingHorizontal: 16,
      paddingTop: 10,
      paddingBottom: 4,
      gap: 4,
    },

    allowanceHelp: {
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 11,
      lineHeight: 15,
      opacity: 0.85,
    },

    // Sent rows carry one action, not three, so it sits left-aligned at its
    // natural width rather than stretching across the card like Accept/Ignore.
    sentActions: {
      flexDirection: "row",
      paddingHorizontal: 14,
      paddingBottom: 12,
      paddingTop: 2,
    },

    ignoreButton: {
      flex: 1,
      minHeight: 38,
      paddingHorizontal: 8,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      borderRadius: 19,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.borderSoft,
      backgroundColor: "transparent",
    },

    ignoreButtonPressed: {
      backgroundColor: colors.surfacePressed,
    },

    ignoreButtonText: {
      color: colors.primaryStrong,
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      fontWeight: "700",
    },

    blockButton: {
      flex: 1,
      minHeight: 38,
      paddingHorizontal: 8,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      borderRadius: 19,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.dangerSoftStrong,
      backgroundColor: "transparent",
    },

    blockButtonPressed: {
      backgroundColor: colors.dangerSoft,
    },

    blockButtonText: {
      color: colors.danger,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "600",
    },

    buttonPressed: {
      opacity: 0.82,
      transform: [{ scale: 0.99 }],
    },

    buttonDisabled: {
      opacity: 0.58,
    },

    empty: {
      flex: 1,
      minHeight: 420,
      paddingHorizontal: 0,
      paddingVertical: Platform.OS === "web" ? 45 : 24,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 0,
      borderWidth: 0,
      borderColor: colors.border,
      backgroundColor: "transparent",
      shadowColor: colors.shadow,
      shadowOpacity: 0,
      shadowRadius: 0,
      shadowOffset: {
        width: 0,
        height: 0,
      },
      elevation: 0,
    },

    emptyGraphic: {
      width: 116,
      height: 116,
      marginBottom: 22,
      alignItems: "center",
      justifyContent: "center",
    },

    emptyOuterRing: {
      position: "absolute",
      width: 116,
      height: 116,
      borderRadius: 58,
      borderWidth: 1,
      borderColor: isDark ? "rgba(110,168,255,0.21)" : "rgba(60,117,210,0.11)",
    },

    emptyMiddleRing: {
      position: "absolute",
      width: 84,
      height: 84,
      borderRadius: 42,
      backgroundColor: colors.primarySoft,
    },

    emptyIcon: {
      width: 58,
      height: 58,
      borderRadius: 29,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.primaryBorder,
      backgroundColor: colors.surface,
      shadowColor: colors.shadow,
      shadowOpacity: isDark ? 0.22 : 0.13,
      shadowRadius: 11,
      shadowOffset: {
        width: 0,
        height: 5,
      },
      elevation: 3,
    },

    emptyCheckBadge: {
      position: "absolute",
      right: 20,
      bottom: 21,
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 3,
      borderColor: colors.surface,
      backgroundColor: colors.green,
    },

    emptyTitle: {
      color: colors.textPrimary,
      fontFamily: FONT_FAMILY,
      fontSize: 22,
      lineHeight: 28,
      fontWeight: "700",
      textAlign: "center",
    },

    emptyDescription: {
      maxWidth: 330,
      marginTop: 9,
      color: colors.textSecondary,
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      lineHeight: 22,
      textAlign: "center",
    },

    emptyStatus: {
      minHeight: 38,
      marginTop: 21,
      paddingHorizontal: 14,
      flexDirection: "row",
      alignItems: "center",
      gap: 7,
      borderRadius: 19,
      backgroundColor: colors.primarySoft,
    },

    emptyStatusText: {
      color: colors.primaryText,
      fontFamily: FONT_FAMILY,
      fontSize: 12,
      fontWeight: "600",
    },
  });
}
